import React, { useRef, useState } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import * as XLSX from 'xlsx';
import RoundButton from './helpercomponents/buttons/RoundButton';
import { useTranslation } from '../../locale/useTranslation';

const FileReaderForm = ({ setFileData }) => {
  const lang = useTranslation();
  const fileInputRef = useRef();

  const [selectedFile, setSelectedFile] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);

  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    const reader = new FileReader();

    const extension = file.name.split('.').pop().toLowerCase();

    if (extension === 'csv' || extension === 'tsv' || extension === 'txt') {
      reader.onload = (event) => {
        const delimiter = extension === 'tsv' ? '\t' : ',';
        const text = event.target.result;
        const lines = text.trim().split('\n');

        if (lines.length === 0) return;

        const headers = lines[0].split(delimiter).map((h) => h.trim());
        const rows = lines.slice(1).map((line) => {
          const values = line.split(delimiter).map((v) => v.trim());
          const obj = {};
          headers.forEach((h, i) => {
            obj[h] = values[i] || '';
          });
          return obj;
        });

        setGridColumns(headers);
        setGridData(rows);
      };

      reader.readAsText(file);
    } else if (extension === 'xlsx') {
      reader.onload = (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false, cellDates: true });

        const formattedData = json.map((row) => {
          const formattedRow = { ...row };
          for (const key in formattedRow) {
            if (formattedRow[key] instanceof Date) {
              formattedRow[key] = new Date(formattedRow[key]).toISOString().slice(0, 19).replace('T', ' ');
            }
          }
          return formattedRow;
        });

        const headers = Object.keys(formattedData[0]);
        setGridColumns(headers);
        setGridData(formattedData);
        setFileData(formattedData);
      };

      reader.readAsArrayBuffer(file);
    } else {
      setGridColumns(['FileName', 'Note']);
      setGridData([{ FileName: file.name, Note: 'Unsupported file type for preview' }]);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setGridColumns([]);
    setGridData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  return (
    <>
      <div className='row'>
        {/* File Upload */}
        <div className='col-12 mt-3'>
          <label>{lang.import_file_label} *</label>
          <div className='d-flex align-items-center'>
            <input
              type='text'
              className='form-control form-control-sm mr-2'
              value={selectedFile ? selectedFile.name : ''}
              readOnly
              placeholder={lang.no_file_selected}
            />
            <RoundButton
              title={lang.select_file_label}
              style={{ width: '33px', marginRight: '5px' }}
              iconColor='white'
              icon='ellipsis-h'
              btnColor='success'
              onClick={handleFileClick}
            />
            {selectedFile && (
              <RoundButton
                title={lang.clear_button_tooltip}
                style={{ width: '35px' }}
                iconColor='white'
                icon='trash'
                btnColor='danger'
                onClick={handleClearFile}
              />
            )}
          </div>
          <input
            type='file'
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept='.csv,.tsv,.txt,.xlsx'
          />
        </div>
      </div>

      {/* Data Grid */}
      {gridData.length > 0 && (
        <div className='row mt-4'>
          <div className='col-12'>
            <Grid data={gridData} style={{ maxHeight: '50vh', overflow: 'auto' }}>
              {gridColumns.map((key, index) => (
                <Column key={index} field={key} title={key} />
              ))}
            </Grid>
          </div>
        </div>
      )}
    </>
  );
};

export default FileReaderForm;
